import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import type { BookingSheetFormProps } from "./types";

interface BookingSheetSharedFieldsProps extends BookingSheetFormProps {
  startTime: Date | null;
  endTime: Date | null;
  courtLabel: string;
  courts: any[];
  courtIds: string[];
  comment: string;
  onCommentChange: (v: string) => void;
  maxCourts?: number;
  hideDate?: boolean;
}

export default function BookingSheetSharedFields({
  formState,
  onFieldChange,
  startTime,
  endTime,
  courtLabel,
  courts,
  courtIds,
  comment: _comment,
  onCommentChange: _onCommentChange,
  maxCourts,
  hideDate,
}: BookingSheetSharedFieldsProps) {
  const { t } = useTranslation("admin");
  // PR-AY2b (mechanical apply of PR-AY2a Tables<> pattern): formState is
  // typed `Record<string, unknown>` in the shared BookingSheetFormProps —
  // a generic refactor of that base type is out of scope for AY2b. Per-site
  // narrowing cast is the mechanical equivalent here.
  const {
    showAdditionalCourts,
    additionalCourts,
    createdBy,
  } = formState as { showAdditionalCourts?: boolean; additionalCourts: string[]; createdBy?: string };

  return (
    <>
      {/* Date/time + court + created by */}
      <div className="space-y-3">
        <div className={hideDate ? "" : "grid grid-cols-2 gap-3"}>
          {!hideDate && (
            <div>
              <Label className="text-xs">{t("bookingSheet.date")}</Label>
              <Input
                type="date"
                value={startTime ? format(startTime, "yyyy-MM-dd") : ""}
                readOnly
                className="h-9 text-sm"
              />
            </div>
          )}
          <div>
            <Label className="text-xs">{t("bookingSheet.time")}</Label>
            <Input
              value={startTime && endTime ? `${format(startTime, "HH:mm")}–${format(endTime, "HH:mm")}` : ""}
              readOnly
              className="h-9 text-sm"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">{t("bookingSheet.court")}</Label>
          {maxCourts === 1 ? (
            <Select
              value={courtIds[0] ?? ""}
              onValueChange={(v) => onFieldChange("primaryCourtId", v)}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder={t("bookingSheet.selectCourt")} />
              </SelectTrigger>
              <SelectContent>
                {courts.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <>
              <Input value={courtLabel} readOnly className="h-9 text-sm" />
              <div className="flex items-center gap-2 mt-1.5">
                <Checkbox
                  id="add-courts"
                  checked={showAdditionalCourts}
                  onCheckedChange={(v) => onFieldChange("showAdditionalCourts", !!v)}
                />
                <label htmlFor="add-courts" className="text-xs text-muted-foreground cursor-pointer">
                  {t("bookingSheet.addMoreCourts")}
                </label>
              </div>
              {showAdditionalCourts && (
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                  {courts.filter((c: any) => !courtIds.includes(c.id)).map((c: any) => (
                    <label key={c.id} className="flex items-center gap-1.5 text-xs">
                      <Checkbox
                        checked={additionalCourts.includes(c.id)}
                        onCheckedChange={(v) => {
                          onFieldChange(
                            "additionalCourts",
                            v ? [...additionalCourts, c.id] : additionalCourts.filter((id: string) => id !== c.id)
                          );
                        }}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div>
          <Label className="text-xs">{t("bookingSheet.createdBy")}</Label>
          <Select value={createdBy} onValueChange={(v) => onFieldChange("createdBy", v)}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="club">{t("bookingSheet.clubAccount")}</SelectItem>
              <SelectItem value="admin">{t("bookingSheet.administrator")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Comment rendered after activity-specific form via the `comment` slot below is NOT here — 
          it's rendered separately at the bottom of the wrapper. This component only has the top shared fields. */}
    </>
  );
}
